import React from "react";
// Custom Fields Modal

// Default
import { useState } from "react";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Custom
import ContainedButton from "../../forms/button/ContainedButton";
import CustomInput from "../../forms/input/Input";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";

// React icons
import { FaAngleLeft } from "react-icons/fa6";
import { MdDelete } from "react-icons/md";
import { RxDragHandleDots2 } from "react-icons/rx";

// Utils
import { useCustomFields } from "../../context/CustomFieldsProvider";
import { ToastState } from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

const CustomFieldsModal = ({
  openModal,
  setOpenModal,
  setToast,
  addOrEdit,
  nodes,
  setNodes,
  handleCustomerAndAssetSearch,
}: {
  openModal?: { id: string; type: string };
  setOpenModal?: (value: { id: string; type: string }) => void;
  setToast?: (value: ToastState) => void;
  addOrEdit?: "add" | "edit" | null;
  nodes?: { id: string; type: string }[];
  setNodes?: (value: { id: string; type: string }[]) => void;
  handleCustomerAndAssetSearch?: () => void;
}) => {
  const {
    createFieldModal,
    toggleCustomFieldModal,
    toggleCustomFieldDrawer,
    setCreatedFields,
    createdFields,
    fieldsDrawer,
  } = useCustomFields();

  const [customField, setCustomField] = useState<{ title: string; type: any }>({
    title: "",
    type: "",
  });

  const [dropdownValues, setDropdownValues] = useState<
    { label: string; value: string }[]
  >([]);
  const [option, setOption] = useState("");

  const customFieldsOptions = [
    { value: "checkbox", label: "Checkbox" },
    { value: "date", label: "Date" },
    { value: "dropdown", label: "Dropdown" },
    { value: "number", label: "Number" },
    { value: "text", label: "Text" },
  ];

  const handleOptionDelete = (idx: number) => {
    const updatedData = dropdownValues?.filter((_, index) => index !== idx);
    setDropdownValues(updatedData);
  };

  const handleCreate = () => {
    let updatedField = null;

    if (customField?.type?.value === "dropdown") {
      updatedField = { ...customField, options: dropdownValues };
    } else {
      updatedField = customField;
    }

    let createdData = null;

    if (!createdFields) {
      createdData = {
        [fieldsDrawer?.type]: [updatedField],
      };
    } else {
      createdData = {
        ...createdFields,
        [fieldsDrawer?.type]: [
          ...(createdFields[fieldsDrawer?.type] || []),
          updatedField,
        ],
      };
    }

    setCreatedFields(createdData);

    toggleCustomFieldDrawer(fieldsDrawer?.type, true);

    toggleCustomFieldModal();

    setCustomField({
      title: "",
      type: "",
    });

    setDropdownValues([]);
  };

  return (
    <Dialog
      onClose={() => toggleCustomFieldModal()}
      open={createFieldModal}
      maxWidth={false}
      sx={{
        "& .MuiPaper-root": {
          width: "500px",
          maxWidth: "none",
        },
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <IconButton
          sx={{ marginLeft: "10px" }}
          onClick={() => {
            toggleCustomFieldModal();
            toggleCustomFieldDrawer(fieldsDrawer?.type, true);
          }}
        >
          <UnifiedIcon icon={FaAngleLeft} color="#0000008a" fontSize="20px" />
        </IconButton>

        <DialogTitle>New field</DialogTitle>
        <IconButton
          onClick={() => toggleCustomFieldModal()}
          sx={{ marginRight: "10px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <DialogContent dividers>
        <Stack direction={"column"} gap={"20px"}>
          <CustomInput
            label={"Title"}
            placeholder={"Add a title..."}
            value={customField?.title}
            onChange={(e) =>
              setCustomField({
                ...customField,
                title: e.target.value,
              })
            }
          />

          <CustomOutlinedSelect
            placeholder={"Select..."}
            options={customFieldsOptions}
            label={"Type"}
            value={customField?.type}
            onChange={(newValue) =>
              setCustomField({
                ...customField,
                type: newValue?.value as string,
              })
            }
          />

          {customField?.type?.value === "dropdown" && (
            <Box>
              <Typography variant="body1" mb={"10px"}>
                Options
              </Typography>

              {dropdownValues?.length > 0 &&
                dropdownValues?.map((item: any, index: number) => {
                  return (
                    <Stack
                      direction={"row"}
                      justifyContent={"space-between"}
                      alignItems={"center"}
                      width={"100%"}
                    >
                      <Stack
                        direction={"row"}
                        gap={"10px"}
                        alignItems={"center"}
                        padding={"10px"}
                      >
                        <UnifiedIcon
                          icon={RxDragHandleDots2}
                          color="#0000008a"
                          fontSize="20px"
                        />
                        <Box
                          sx={{
                            width: "30px",
                            height: "30px",
                            backgroundColor: "#7D808F",
                            borderRadius: "2px",
                          }}
                        ></Box>
                        <Typography>{item?.label}</Typography>
                      </Stack>
                      <IconButton onClick={() => handleOptionDelete(index)}>
                        <UnifiedIcon
                          icon={MdDelete}
                          color="#0000008a"
                          fontSize="22px"
                        />
                      </IconButton>
                    </Stack>
                  );
                })}

              <Stack
                direction={"row"}
                gap={"10px"}
                mt={"10px"}
                alignItems={"center"}
              >
                <CustomInput
                  placeholder={"Add item..."}
                  value={option}
                  onChange={(e) => setOption(e.target.value)}
                />

                <ContainedButton
                  label={"Add"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    height: "39px",
                    mt: "8px",
                    color: "#ffffff",
                  }}
                  onClick={() => {
                    setDropdownValues([
                      ...dropdownValues,
                      { label: option, value: option },
                    ]);
                    setOption("");
                  }}
                  disabled={!option}
                />
              </Stack>
            </Box>
          )}

          <ContainedButton
            label={"Create"}
            sx={{
              width: "100%",
              fontWeight: 600,
              color: "#ffffff",
            }}
            disabled={!customField?.title && !customField?.type}
            onClick={handleCreate}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CustomFieldsModal;
